import * as React from 'react';

import { canSelectAll } from './can-select-all.js';
import { isAllSelected } from './is-all-selected.js';

type Option = Readonly<{ value: string; disabled?: boolean }>;

type FilterSelectAll = Readonly<{
  /** Whether Select All is offered: once two or more options can be picked, as `DimeAutocomplete` offers it, and every option but one fits under `maxPicks`. */
  offered: boolean;
  /** The picks to show: every option that can be picked while Select All is on, though the caller holds none. */
  value: ReadonlyArray<string>;
  onValueChange: (next: string[]) => void;
  allPicked: boolean;
  toggleAll: () => void;
  /** Ends Select All's display, for a reset the caller can't signal through `value` (it is `[]` either way). */
  reset: () => void;
}>;

/**
 * Select All for a multi-pick filter, sent as no filter (`[]`) so rows matching none of the options
 * are never dropped, while the rows keep showing ticked; hand picks are always sent as picked.
 */
export function useFilterSelectAll({
  options,
  value,
  onValueChange,
  enabled = true,
  maxPicks,
}: Readonly<{
  options: ReadonlyArray<Option>;
  value: ReadonlyArray<string>;
  onValueChange: (next: string[]) => void;
  enabled?: boolean;
  /** A cap on how many values the filter accepts; Select All is offered only if every option but one fits, as unticking one after it sends the rest. */
  maxPicks?: number;
}>): FilterSelectAll {
  // `from` is the picks Select All was pressed from until its no filter lands, as a URL-backed caller commits it a render late.
  const [selectAll, setSelectAll] = React.useState<{ from: string | null } | null>(null);
  const valueKey = value.join('\n');
  if (selectAll !== null && value.length === 0 && selectAll.from !== null) {
    setSelectAll({ from: null });
  }
  // A pick made from outside (a URL, a chip) ends Select All.
  if (selectAll !== null && value.length > 0 && valueKey !== selectAll.from) setSelectAll(null);
  const everyPicked = selectAll !== null;
  // No filter takes in a disabled option's rows too, so a disabled option doesn't hold Select All back.
  // Ticks only what can be picked, so unticking one row after Select All never sends a disabled one.
  const pickableValues = options
    .filter((option) => option.disabled !== true)
    .map((option) => option.value);
  const offered =
    enabled &&
    canSelectAll(options) &&
    (maxPicks === undefined || pickableValues.length - 1 <= maxPicks);
  const shown = offered && everyPicked && value.length === 0 ? pickableValues : value;
  const allPicked = offered && isAllSelected(options, shown);
  return {
    offered,
    value: shown,
    onValueChange: (next) => {
      setSelectAll(null);
      onValueChange(next);
    },
    allPicked,
    toggleAll: () => {
      setSelectAll(allPicked ? null : { from: value.length > 0 ? valueKey : null });
      onValueChange([]);
    },
    reset: () => setSelectAll(null),
  };
}
